import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const UnderstandingBusinessCapital = () => {
  return (
    <>
      <Helmet>
        <title>Understanding Business Capital: A Complete Guide - Miranda Landa Services LLC</title>
        <meta name="description" content="Learn about different types of business capital, how to assess your needs, and prepare for funding opportunities." />
      </Helmet>

      {/* Hero */}
      <section style={{
        position: 'relative',
        minHeight: '500px',
        display: 'flex',
        alignItems: 'center',
        color: 'white',
        padding: '4rem 2rem',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'url(https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1920&h=800&fit=crop&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'brightness(0.4)'
        }} />
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(5, 150, 105, 0.9) 0%, rgba(16, 185, 129, 0.8) 100%)'
        }} />
        <div style={{position: 'relative', zIndex: 1, maxWidth: '900px', margin: '0 auto', width: '100%'}}>
          <div style={{marginBottom: '1rem'}}>
            <Link to="/blog" style={{color: 'white', opacity: 0.9}}>← Back to Blog</Link>
          </div>
          <div style={{
            background: '#f0fdf4',
            color: '#059669',
            padding: '0.5rem 1rem',
            borderRadius: '20px',
            display: 'inline-block',
            fontSize: '0.9rem',
            fontWeight: '600',
            marginBottom: '1rem'
          }}>
            Funding Basics
          </div>
          <h1 style={{fontSize: '3rem', fontWeight: '800', marginBottom: '1rem', lineHeight: '1.2'}}>
            Understanding Business Capital: A Complete Guide
          </h1>
          <p style={{fontSize: '1.1rem', opacity: 0.9}}>
            Published: December 1, 2024 | 8 min read
          </p>
        </div>
      </section>

      {/* Content */}
      <section style={{padding: '4rem 2rem', background: 'white'}}>
        <div style={{maxWidth: '800px', margin: '0 auto'}}>
          
          <div style={{marginBottom: '3rem'}}>
            <p style={{fontSize: '1.2rem', color: '#4b5563', lineHeight: '1.8', marginBottom: '2rem'}}>
              Business capital is the lifeblood of any growing company. Whether you're starting a new venture or expanding 
              an existing business, understanding the different types of capital and how to access them is crucial for success.
            </p>

            <img 
              src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=500&fit=crop&q=80"
              alt="Business planning"
              style={{width: '100%', height: '400px', objectFit: 'cover', borderRadius: '12px', marginBottom: '2rem'}}
            />
          </div>

          <div style={{marginBottom: '3rem'}}>
            <h2 style={{fontSize: '2rem', fontWeight: '700', color: '#064e3b', marginBottom: '1.5rem'}}>
              What is Business Capital?
            </h2>
            <p style={{fontSize: '1.1rem', color: '#4b5563', lineHeight: '1.8', marginBottom: '1.5rem'}}>
              Business capital refers to the financial resources that a company uses to fund its operations, growth, and 
              strategic initiatives. It can come from various sources and take different forms, each with its own advantages 
              and considerations.
            </p>
          </div>

          <div style={{marginBottom: '3rem'}}>
            <h2 style={{fontSize: '2rem', fontWeight: '700', color: '#064e3b', marginBottom: '1.5rem'}}>
              Types of Business Capital
            </h2>
            
            <div style={{marginBottom: '2rem'}}>
              <h3 style={{fontSize: '1.5rem', fontWeight: '600', color: '#059669', marginBottom: '1rem'}}>
                1. Working Capital
              </h3>
              <p style={{fontSize: '1.1rem', color: '#4b5563', lineHeight: '1.8'}}>
                Working capital is the money used for day-to-day operations. It covers expenses like payroll, rent, utilities, 
                and inventory. Having adequate working capital ensures your business can operate smoothly without cash flow interruptions.
              </p>
            </div>

            <div style={{marginBottom: '2rem'}}>
              <h3 style={{fontSize: '1.5rem', fontWeight: '600', color: '#059669', marginBottom: '1rem'}}>
                2. Growth Capital
              </h3>
              <p style={{fontSize: '1.1rem', color: '#4b5563', lineHeight: '1.8'}}>
                Growth capital is used to expand your business. This might include opening new locations, launching new products, 
                or entering new markets. It's typically used for strategic initiatives that will increase revenue.
              </p>
            </div>

            <div style={{marginBottom: '2rem'}}>
              <h3 style={{fontSize: '1.5rem', fontWeight: '600', color: '#059669', marginBottom: '1rem'}}>
                3. Equipment Capital
              </h3>
              <p style={{fontSize: '1.1rem', color: '#4b5563', lineHeight: '1.8'}}>
                Equipment capital is specifically for purchasing machinery, vehicles, technology, or other equipment needed 
                to run or grow your business. This type of financing is often secured by the equipment itself.
              </p>
            </div>
          </div>

          <div style={{
            background: '#f0fdf4',
            padding: '2rem',
            borderRadius: '12px',
            borderLeft: '4px solid #10b981',
            marginBottom: '3rem'
          }}>
            <h3 style={{fontSize: '1.3rem', fontWeight: '700', color: '#064e3b', marginBottom: '1rem'}}>
              💡 Expert Tip
            </h3>
            <p style={{color: '#065f46', lineHeight: '1.7'}}>
              Before seeking capital, clearly define what you need the funds for and how they will help your business grow. 
              Lenders and investors want to see a clear plan for how their capital will be used and how it will generate returns.
            </p>
          </div>

          <div style={{marginBottom: '3rem'}}>
            <h2 style={{fontSize: '2rem', fontWeight: '700', color: '#064e3b', marginBottom: '1.5rem'}}>
              How to Assess Your Capital Needs
            </h2>
            <p style={{fontSize: '1.1rem', color: '#4b5563', lineHeight: '1.8', marginBottom: '1.5rem'}}>
              Determining how much capital you need requires careful analysis:
            </p>
            <ul style={{fontSize: '1.1rem', color: '#4b5563', lineHeight: '1.8', paddingLeft: '2rem'}}>
              <li>Review your current financial statements</li>
              <li>Project your future cash flow needs</li>
              <li>Calculate the cost of your growth initiatives</li>
              <li>Build in a buffer for unexpected expenses</li>
              <li>Consider the timing of when you'll need the funds</li>
            </ul>
          </div>

          <div style={{marginBottom: '3rem'}}>
            <h2 style={{fontSize: '2rem', fontWeight: '700', color: '#064e3b', marginBottom: '1.5rem'}}>
              Preparing for Funding
            </h2>
            <p style={{fontSize: '1.1rem', color: '#4b5563', lineHeight: '1.8', marginBottom: '1.5rem'}}>
              Once you understand your capital needs, preparation is key:
            </p>
            <div style={{
              background: '#f9fafb',
              padding: '2rem',
              borderRadius: '12px',
              border: '1px solid #e5e7eb'
            }}>
              <ol style={{fontSize: '1.1rem', color: '#4b5563', lineHeight: '1.8', paddingLeft: '2rem'}}>
                <li style={{marginBottom: '1rem'}}><strong>Organize Financial Documents:</strong> Gather tax returns, financial statements, and business plans</li>
                <li style={{marginBottom: '1rem'}}><strong>Improve Credit Score:</strong> Both personal and business credit matter</li>
                <li style={{marginBottom: '1rem'}}><strong>Create Projections:</strong> Develop realistic financial forecasts</li>
                <li style={{marginBottom: '1rem'}}><strong>Build Relationships:</strong> Connect with potential lenders early</li>
                <li><strong>Seek Expert Guidance:</strong> Work with consultants who understand the funding landscape</li>
              </ol>
            </div>
          </div>

          <div style={{
            background: '#fef3c7',
            padding: '2rem',
            borderRadius: '12px',
            border: '2px solid #f59e0b',
            marginBottom: '3rem',
            textAlign: 'center'
          }}>
            <p style={{color: '#92400e', fontWeight: '700', fontSize: '1.1rem', marginBottom: '1rem'}}>
              ⚠️ Important Reminder
            </p>
            <p style={{color: '#78350f', lineHeight: '1.7'}}>
              Miranda Landa Services LLC is a consulting firm that provides guidance and preparation services. 
              We are NOT a lender and do not make lending decisions. All financing is provided by third-party financial institutions.
            </p>
          </div>

          {/* CTA */}
          <div style={{
            background: 'linear-gradient(135deg, #064e3b 0%, #059669 100%)',
            color: 'white',
            padding: '3rem 2rem',
            borderRadius: '16px',
            textAlign: 'center'
          }}>
            <h3 style={{fontSize: '2rem', fontWeight: '700', marginBottom: '1rem'}}>
              Need Help Understanding Your Capital Needs?
            </h3>
            <p style={{fontSize: '1.1rem', marginBottom: '2rem', opacity: 0.95}}>
              Our expert consultants can help you assess your funding requirements and prepare for success
            </p>
            <Link to="/contact" style={{
              background: 'white',
              color: '#059669',
              padding: '1rem 2.5rem',
              borderRadius: '50px',
              fontWeight: '700',
              fontSize: '1.1rem',
              display: 'inline-block'
            }}>
              Schedule Free Consultation
            </Link>
          </div>

          {/* Related Posts */}
          <div style={{marginTop: '4rem'}}>
            <h3 style={{fontSize: '1.8rem', fontWeight: '700', color: '#064e3b', marginBottom: '2rem'}}>
              Related Articles
            </h3>
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '2rem'}}>
              <Link to="/blog/preparing-for-funding" style={{textDecoration: 'none'}}>
                <div className="card" style={{height: '100%'}}>
                  <img 
                    src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=200&fit=crop&q=80"
                    alt="Preparing for funding"
                    style={{width: '100%', height: '150px', objectFit: 'cover', borderRadius: '8px', marginBottom: '1rem'}}
                  />
                  <h4 style={{color: '#064e3b', fontSize: '1.2rem', fontWeight: '600', marginBottom: '0.5rem'}}>
                    5 Steps to Prepare for Funding
                  </h4>
                  <p style={{color: '#6b7280', fontSize: '0.9rem'}}>
                    Essential preparation steps before seeking capital
                  </p>
                </div>
              </Link>
              <Link to="/blog/choosing-right-lender" style={{textDecoration: 'none'}}>
                <div className="card" style={{height: '100%'}}>
                  <img 
                    src="https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=400&h=200&fit=crop&q=80"
                    alt="Choosing lender"
                    style={{width: '100%', height: '150px', objectFit: 'cover', borderRadius: '8px', marginBottom: '1rem'}}
                  />
                  <h4 style={{color: '#064e3b', fontSize: '1.2rem', fontWeight: '600', marginBottom: '0.5rem'}}>
                    Choosing the Right Lender
                  </h4>
                  <p style={{color: '#6b7280', fontSize: '0.9rem'}}>
                    How to evaluate and select the best lender for your needs
                  </p>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default UnderstandingBusinessCapital;
